import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Info,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Landmark,
  Activity,
  Building2,
} from "lucide-react";
import SignalBadge from "@/components/common/SignalBadge";
import SectorVerdictPill, { sectorVerdictTheme } from "@/components/common/SectorVerdictPill";
import ScoreGauge from "@/components/common/ScoreGauge";
import { formatLKR, formatPct } from "@/lib/api";

/**
 * FiveLayerBreakdownPanel — Phase 6 Stage 3
 *
 * Detailed Phase-6 composite score breakdown for the Stock Detail Overview tab.
 *
 * Sections:
 *   1. Header   — gauge + signal + data confidence + AI one-liner
 *   2. Layers   — five horizontal bars with sub-metrics + special indicators
 *   3. Technical bonus row (separate from layers)
 *
 * Investment trusts collapse to a simplified panel that only shows layers
 * 2/3/5 + the explanatory note.
 */

const LAYER_DESCRIPTIONS = {
  layer1: "Measures how cheap the stock is RIGHT NOW compared to its sector peers on PE ratio, book value, and dividend yield.",
  layer2: "Measures whether the business is improving or declining over time based on earnings growth, revenue growth, and dividend payment history.",
  layer3: "Measures the strength of the company's balance sheet — how much debt it carries and how efficiently it generates returns.",
  layer4: "Compares the current price to two theoretical fair values — Benjamin Graham's asset-based formula and a dividend discount model.",
  layer5: "Incorporates Claude AI analysis of company reports and news sentiment to capture insights that financial ratios cannot show.",
  layer6: "Sector Context — Claude AI scores the sector itself once a week on cycle position, valuation vs history, macro tailwinds, and foreign-investor sentiment. Same score applies to all stocks in the sector.",
};

// Confidence styling
const CONFIDENCE_BADGE = {
  high:   { label: "High confidence",   dot: "bg-success",        border: "border-success/30",      text: "text-success" },
  medium: { label: "Medium confidence", dot: "bg-warning",        border: "border-warning/40",      text: "text-warning" },
  low:    { label: "Low confidence",    dot: "bg-muted-foreground", border: "border-border",        text: "text-muted-foreground" },
  none:   { label: "No data",           dot: "bg-muted-foreground/60", border: "border-border",     text: "text-muted-foreground" },
};

// Score-to-tone mapping for layer bars (based on % of max)
const layerTone = (got, max) => {
  if (max <= 0) return "neutral";
  const p = got / max;
  if (p >= 0.75) return "good";
  if (p >= 0.5)  return "ok";
  return "bad";
};

const TONE_BAR = {
  good: "bg-success",
  ok:   "bg-warning",
  bad:  "bg-destructive",
  neutral: "bg-muted-foreground/40",
};

const TONE_TRACK = {
  good: "bg-success/15",
  ok:   "bg-warning/15",
  bad:  "bg-destructive/15",
  neutral: "bg-muted",
};

const InfoTip = ({ text }) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="More info"
          className="inline-flex items-center text-muted-foreground/70 hover:text-foreground transition-colors"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-snug">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ProgressBar = ({ value, max, tone }) => {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className={`h-1.5 w-full rounded-full ${TONE_TRACK[tone] || TONE_TRACK.neutral}`}>
      <div
        className={`h-full rounded-full transition-all ${TONE_BAR[tone] || TONE_BAR.neutral}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const LayerRow = ({
  index,
  title,
  tooltip,
  score,
  maxScore,
  subMetrics,        // [{ label, score?, max?, value?, indicator?: 'good'|'bad'|'warning'|'banking', estimated?: bool }]
  hideOnMobileSubs,
  testId,
}) => {
  const tone = layerTone(score, maxScore);
  const pct = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-[11px] font-mono text-muted-foreground tabular-nums shrink-0">
            L{index}
          </span>
          <span className="text-sm font-medium truncate">{title}</span>
          {tooltip && <InfoTip text={tooltip} />}
        </div>
        <div className="flex items-baseline gap-2 shrink-0">
          <span className="font-mono text-sm tabular-nums font-semibold">
            {score}<span className="text-muted-foreground font-normal">/{maxScore}</span>
          </span>
          <span className="text-[11px] text-muted-foreground tabular-nums w-9 text-right">
            {pct}%
          </span>
        </div>
      </div>

      <ProgressBar value={score} max={maxScore} tone={tone} />

      {subMetrics && subMetrics.length > 0 && (
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 pt-0.5 ${
            hideOnMobileSubs ? "hidden sm:grid" : ""
          }`}
        >
          {subMetrics.map((m, i) => (
            <div
              key={i}
              className="flex items-center justify-between gap-2 text-[11.5px] text-muted-foreground"
            >
              <span className="flex items-center gap-1 min-w-0 truncate">
                {m.label}
                {m.value != null && (
                  <span className="text-foreground/80 font-mono tabular-nums">
                    ({m.value})
                  </span>
                )}
                {m.estimated && <span className="text-[10px]">(est.)</span>}
                {m.indicator === "good" && (
                  <CheckCircle2 className="h-3 w-3 text-success shrink-0" aria-label="below fair value" />
                )}
                {m.indicator === "bad" && (
                  <XCircle className="h-3 w-3 text-destructive shrink-0" aria-label="above fair value" />
                )}
                {m.indicator === "warning" && (
                  <AlertTriangle className="h-3 w-3 text-warning shrink-0" aria-label="warning" />
                )}
                {m.indicator === "banking" && (
                  <Landmark className="h-3 w-3 text-info shrink-0" aria-label="banking thresholds" />
                )}
              </span>
              {m.score != null && m.max != null && (
                <span className="font-mono tabular-nums text-foreground/80 shrink-0">
                  {m.score}/{m.max}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helpers
const fmtSigned = (n, suffix = "%") => {
  if (n == null || Number.isNaN(n)) return null;
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${Math.abs(n).toFixed(n >= 100 || n <= -100 ? 0 : 1)}${suffix}`;
};

const FiveLayerBreakdownPanel = ({ score, aiOneLiner }) => {
  // Graceful empty state — fail soft if score doc not loaded yet
  if (!score) {
    return (
      <Card className="border bg-card" data-testid="five-layer-panel-empty">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Composite score still computing — try refreshing in a moment.
        </CardContent>
      </Card>
    );
  }

  const final = score.final_score ?? score.composite_score ?? 0;
  const signal = score.signal || score.composite_signal;
  const conf = CONFIDENCE_BADGE[score.data_confidence] || CONFIDENCE_BADGE.none;
  const isTrust = !!score.is_investment_trust;
  const isBanking = ["Banks", "Banking", "Diversified Financials"].includes(score.sector || "");
  const isSpecialDiv = !!score.special_dividend_detected;

  // Banking + special-dividend warnings (UI cues only — already affect scoring)
  const grahamFv = score.layer4_graham_number;
  const ddmFv = score.layer4_ddm_fair_value;
  const price = score.current_price;
  const grahamCheap = grahamFv && price && price < grahamFv;
  const ddmCheap = ddmFv && price && price < ddmFv;

  // Dividend yield estimated? Heuristic — if dividend_yield exists but layer1
  // yield is using the raw value (not capped) and there's no special flag, we
  // can't reliably tell from here. We'll defer the (est.) tag to fundamentals.
  const yieldEstimated = false;

  // ---- Build layer specs ----
  const layer1 = {
    title: "Current Value",
    tooltip: LAYER_DESCRIPTIONS.layer1,
    score: score.layer1_score ?? 0,
    max: 25,
    subs: [
      {
        label: "PE vs sector",
        score: score.layer1_pe_score ?? 0,
        max: 10,
        value: score.pe_ratio != null ? score.pe_ratio.toFixed(2) : "—",
      },
      {
        label: "Price/Book",
        score: score.layer1_pb_score ?? 0,
        max: 5,
        value: score.pb_ratio != null ? score.pb_ratio.toFixed(2) : "—",
      },
      {
        label: "Dividend yield",
        score: score.layer1_yield_score ?? 0,
        max: 10,
        value: score.dividend_yield != null ? formatPct(score.dividend_yield) : "—",
        indicator: isSpecialDiv ? "warning" : null,
        estimated: yieldEstimated,
      },
    ],
  };

  const epsBasis = score.layer2_eps_growth_basis;
  const epsHistoryYears = score.layer2_eps_history_years || 0;
  const epsCagr = score.layer2_eps_cagr;
  const isLumpy = !!score.layer2_lumpy_earnings;

  let epsGrowthLabel = "EPS growth";
  let epsGrowthValue = null;
  if (epsBasis === "cagr" && epsCagr != null) {
    epsGrowthLabel = `EPS CAGR (${epsHistoryYears}y)`;
    epsGrowthValue = formatPct(epsCagr);
  } else if (epsBasis === "median_capped") {
    epsGrowthLabel = `EPS growth — median-capped (${epsHistoryYears}y)`;
  } else if (epsBasis === "yoy") {
    epsGrowthLabel = "EPS growth (TTM YoY)";
  } else if (epsBasis === "missing") {
    epsGrowthLabel = "EPS growth — no data";
  }

  const layer2 = {
    title: "Historical Quality",
    tooltip: LAYER_DESCRIPTIONS.layer2,
    score: score.layer2_score ?? 0,
    max: 25,
    subs: [
      {
        label: epsGrowthLabel,
        score: score.layer2_eps_growth_score ?? 0,
        max: 10,
        value: epsGrowthValue,
        indicator: isLumpy ? "warning" : null,
      },
      {
        label: "Revenue growth",
        score: score.layer2_revenue_growth_score ?? 0,
        max: 8,
      },
      {
        label: `Dividend consistency${
          score.layer2_consecutive_dividend_years != null
            ? ` (${score.layer2_consecutive_dividend_years} yrs)`
            : ""
        }`,
        score: score.layer2_dividend_consistency_score ?? 0,
        max: 7,
      },
    ],
  };

  const layer3 = {
    title: "Financial Health",
    tooltip: LAYER_DESCRIPTIONS.layer3,
    score: score.layer3_score ?? 0,
    max: 15,
    subs: [
      {
        label: "Debt / Equity",
        score: score.layer3_debt_score ?? 0,
        max: 8,
        indicator: isBanking ? "banking" : null,
      },
      {
        label: "ROE",
        score: score.layer3_roe_score ?? 0,
        max: 7,
        value: score.roe != null ? formatPct(score.roe) : "—",
      },
    ],
  };

  const layer4 = {
    title: "Intrinsic Value",
    tooltip: LAYER_DESCRIPTIONS.layer4,
    score: score.layer4_score ?? 0,
    max: 20,
    subs: [
      {
        label: "Graham Number",
        score: score.layer4_graham_score ?? 0,
        max: 10,
        value: grahamFv != null ? formatLKR(grahamFv) : null,
        indicator: grahamFv ? (grahamCheap ? "good" : "bad") : null,
      },
      {
        label: "DDM Fair Value",
        score: score.layer4_ddm_score ?? 0,
        max: 10,
        value: ddmFv != null ? formatLKR(ddmFv) : null,
        indicator: ddmFv ? (ddmCheap ? "good" : "bad") : (isSpecialDiv ? "warning" : null),
      },
    ],
  };

  const layer5 = {
    title: "Forward Intelligence",
    tooltip: LAYER_DESCRIPTIONS.layer5,
    score: score.layer5_score ?? 0,
    max: 15,
    subs: [
      {
        label: `AI signal${score.layer5_ai_signal ? ` (${score.layer5_ai_signal})` : ""}`,
        score: score.layer5_ai_signal_score ?? 0,
        max: 8,
      },
      {
        label: `News sentiment${
          score.layer5_news_counts?.total
            ? ` (${score.layer5_news_counts.total} articles)`
            : ""
        }`,
        score: score.layer5_news_sentiment_score ?? 0,
        max: 7,
      },
    ],
  };

  // Layer 6 — Sector Context (cached weekly, applies to all stocks in this sector)
  const l6Verdict = (score.layer6_verdict || "").toUpperCase();
  const l6Note = score.layer6_note;
  const l6Stale = !!score.layer6_stale;
  const layer6 = {
    title: "Sector Context",
    tooltip: LAYER_DESCRIPTIONS.layer6,
    score: score.layer6_score ?? 0,
    max: 10,
    subs: [
      {
        label: `Cycle${score.layer6_cycle_position ? ` (${score.layer6_cycle_position.replace(/_/g, " ")})` : ""}`,
      },
      {
        label: `Macro${score.layer6_macro ? ` (${score.layer6_macro})` : ""}`,
      },
      {
        label: `Foreign${score.layer6_foreign ? ` (${score.layer6_foreign})` : ""}`,
      },
      l6Verdict
        ? { label: `Verdict: ${l6Verdict}`, indicator: l6Verdict === "OVERWEIGHT" ? "good" : l6Verdict === "UNDERWEIGHT" ? "bad" : null }
        : { label: "Verdict: pending weekly run", indicator: "warning" },
    ],
  };

  // Layers shown depends on company type. For investment trusts: drop L1 + L4
  // (which were nullified in scoring) and surface a callout instead.
  // Layer 6 (Sector Context) always applies — it's a sector-level overlay.
  const layers = isTrust
    ? [layer2, layer3, layer5, layer6]
    : [layer1, layer2, layer3, layer4, layer5, layer6];
  const layerIndices = isTrust ? [2, 3, 5, 6] : [1, 2, 3, 4, 5, 6];

  // Technical bonus
  const tb = score.technical_bonus ?? 0;
  const tvSignal = score.tv_signal || score.tv_recommendation || null;
  const rsi = score.rsi;
  const rsiAdj = score.rsi_adjustment ?? 0;
  const lowVol = !!score.low_volume_warning;
  const tbColor = tb > 0 ? "text-success" : tb < 0 ? "text-destructive" : "text-muted-foreground";

  return (
    <Card className="border bg-card" data-testid="five-layer-panel">
      <CardContent className="p-5 sm:p-6 space-y-5">
        {/* HEADER — gauge + signal + confidence + one-liner */}
        <div className="flex items-start gap-5 flex-wrap sm:flex-nowrap">
          <div className="shrink-0">
            <ScoreGauge score={final} label="Composite" />
          </div>
          <div className="flex-1 min-w-[12rem]">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Composite Score
              </span>
              <SignalBadge signal={signal} data-testid="composite-signal" />
              <Badge
                variant="outline"
                className={`text-[10px] gap-1.5 px-1.5 py-0 ${conf.border} ${conf.text}`}
                data-testid="data-confidence-badge"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${conf.dot}`} />
                {conf.label}
              </Badge>
              {isTrust && (
                <Badge
                  variant="outline"
                  className="text-[10px] gap-1 px-1.5 py-0 border-info/40 text-info"
                >
                  <Building2 className="h-3 w-3" /> Investment trust
                </Badge>
              )}
              {isBanking && (
                <Badge
                  variant="outline"
                  className="text-[10px] gap-1 px-1.5 py-0 border-info/40 text-info"
                >
                  <Landmark className="h-3 w-3" /> Banking sector
                </Badge>
              )}
            </div>
            {aiOneLiner && (
              <div className="mt-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                  AI analysis
                </div>
                <p className="text-sm leading-snug text-muted-foreground line-clamp-3" data-testid="five-layer-ai-rationale">
                  {aiOneLiner}
                </p>
              </div>
            )}
            {isTrust && (
              <p className="mt-2 text-[12px] text-muted-foreground italic">
                Valuation model adjusted for investment trust classification.
                PE and Graham Number not scored.
              </p>
            )}
            {isSpecialDiv && (score.scoring_notes || []).length > 0 && (
              <div className="mt-2 flex items-start gap-1.5 text-[12px] text-warning">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span className="leading-snug">{score.scoring_notes[0]}</span>
              </div>
            )}
            {isLumpy && (
              <div
                className="mt-2 flex items-start gap-1.5 text-[12px] text-warning rounded-md border border-warning/30 bg-warning/5 p-2"
                data-testid="lumpy-earnings-warning"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span className="leading-snug">
                  <strong>Lumpy earnings detected.</strong>{" "}
                  Latest EPS{" "}
                  {score.layer2_latest_eps != null && (
                    <span className="font-mono">({score.layer2_latest_eps.toFixed(2)})</span>
                  )}{" "}
                  is{" "}
                  {score.layer2_eps_spike_ratio != null && (
                    <span className="font-mono">{score.layer2_eps_spike_ratio.toFixed(2)}×</span>
                  )}{" "}
                  the historical median{" "}
                  {score.layer2_median_eps != null && (
                    <span className="font-mono">({score.layer2_median_eps.toFixed(2)})</span>
                  )}{" "}
                  — a single-year windfall. Layer 2 EPS-growth score has been
                  capped using median growth.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* LAYERS */}
        <div className="space-y-4 pt-2 border-t border-border/60">
          {layers.map((l, i) => (
            <LayerRow
              key={i}
              index={layerIndices[i]}
              title={l.title}
              tooltip={l.tooltip}
              score={l.score}
              maxScore={l.max}
              subMetrics={l.subs}
              hideOnMobileSubs
              testId={`layer-row-${layerIndices[i]}`}
            />
          ))}
        </div>

        {/* LAYER 6 — Sector summary callout (only when populated)
            Phase 11 polish — context box bg + left border match the
            verdict colour (green / blue / red). */}
        {(score.layer6_summary || score.layer6_verdict || l6Note) && (() => {
          const theme = sectorVerdictTheme(l6Verdict);
          return (
            <div
              className={`rounded-md ${theme.boxBg} ${theme.boxBorder} p-3 text-[12px] leading-snug`}
              data-testid="layer6-sector-callout"
            >
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <span className={`text-[10px] uppercase tracking-wide font-bold ${theme.boxText}`}>
                  Sector Context · Layer 6
                </span>
                {l6Verdict && (
                  <SectorVerdictPill
                    verdict={l6Verdict}
                    size="sm"
                    testId="layer6-verdict-pill"
                  />
                )}
                {l6Stale && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 border-warning/40 text-warning gap-1"
                  >
                    <AlertTriangle className="h-3 w-3" /> Stale (&gt;7d)
                  </Badge>
                )}
                {l6Note === "sector_not_analysed" && (
                  <span className="text-[10px] text-muted-foreground italic">
                    Pending first weekly run
                  </span>
                )}
              </div>
              {score.layer6_summary && (
                <p className="text-foreground/90">{score.layer6_summary}</p>
              )}
              {score.layer6_analysed_at && (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Analysed {new Date(score.layer6_analysed_at).toLocaleDateString()}
                </p>
              )}
            </div>
          );
        })()}

        {/* TECHNICAL BONUS */}
        <div className="rounded-md border border-border/70 bg-muted/30 p-3" data-testid="technical-bonus-row">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Technical Adjustment</span>
              <InfoTip text="Short-term market timing adjustment from TradingView's signal panel and RSI extremes. Capped at ±10 and halved on illiquid stocks." />
            </div>
            <span className={`font-mono text-sm tabular-nums font-semibold ${tbColor}`}>
              {tb > 0 ? "+" : ""}
              {tb} pts
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-[11.5px] text-muted-foreground">
            <div className="flex items-center justify-between gap-2">
              <span>TV Signal</span>
              {tvSignal ? (
                <span className="inline-flex items-center gap-1.5">
                  <SignalBadge signal={tvSignal} size="sm" />
                  <span className="font-mono text-foreground/80 tabular-nums">
                    ({score.tv_bonus_component >= 0 ? "+" : ""}{score.tv_bonus_component ?? 0})
                  </span>
                </span>
              ) : (
                <span className="font-mono text-foreground/60">—</span>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span>RSI</span>
              <span className="font-mono text-foreground/80">
                {rsi != null ? rsi.toFixed(1) : "—"}
                {rsi != null && (
                  <span className="ml-1 text-[10px]">
                    {rsi > 80
                      ? `(overbought ${fmtSigned(rsiAdj, "")})`
                      : rsi < 25
                      ? `(oversold ${fmtSigned(rsiAdj, "")})`
                      : "(neutral)"}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span>Low volume</span>
              <span className="font-mono text-foreground/80">
                {lowVol ? "Yes (TV bonus halved)" : "No"}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FiveLayerBreakdownPanel;
